import { getSubscriptionAudience, type SubscriptionPublic } from '@hero/schema';
import { useCurrentUser } from '@hero/hooks-shared';
import type { SubscriptionAudience } from '@hero/core';

/**
 * Which price list to quote the signed-in account from.
 *
 * An owner-operator and a company do not pay the same rate per order
 * (see `USAGE_ORDER_TIERS` in `subscription.config.ts`), so every price
 * the checkout renders has to name an audience or it silently quotes
 * company rates to a freelancer — a number they will never be charged.
 *
 * Two sources, in this order:
 *
 * 1. **The subscription's owner**, when one exists. Authoritative: it is
 *    the Party the grant is actually held by, and it is what the backend
 *    will read when it prices the purchase.
 * 2. **The current user's shape**, before any subscription exists. A
 *    driver with no org membership is an owner-operator; everyone else
 *    buys as a company.
 *
 * The two agree by construction — a freelancer's subscription is owned
 * by their login, carrying the `driver` role that marks them as one —
 * so the fallback is not a guess, just the same answer available one
 * request earlier.
 */
export function useSubscriptionAudience(
  subscription?: Pick<SubscriptionPublic, 'owner'> | null,
): SubscriptionAudience {
  const { isFreelancerDriver } = useCurrentUser();
  if (subscription) return getSubscriptionAudience(subscription);
  return isFreelancerDriver ? 'freelancer' : 'org';
}
